import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCustomerStage } from "@/hooks/useCustomerStage";
import { stageDashboardMap } from "@/utils/navigationUtils";
import Logo from "./Logo";
import { Button } from "./ui/button";
import { useState, useEffect, useRef } from "react";

interface HeaderProps {
  logoVariant?: "default" | "white";
  onGetStartedClick?: () => void;
  /** On /queued: recolor the waitlist CTA green + relabel "You're on the list". */
  queued?: boolean;
  /** Splash pages only: below md, render an in-flow brown bar with a hamburger
   *  menu + Join button (meant to sit inside the page's sticky group) instead
   *  of the absolute overlay header. Desktop is unchanged. */
  mobileRedesign?: boolean;
}

const scrollToId = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const Header = ({ logoVariant = "default", onGetStartedClick, queued = false, mobileRedesign = false }: HeaderProps) => {
  const { user, signOut } = useAuth();
  const { stage } = useCustomerStage();
  const navigate = useNavigate();
  const location = useLocation();
  const isMints = location.pathname === '/mints';
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Change header appearance after scrolling past ~500px (past the brown hero section)
      setIsScrolled(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDashboardClick = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    const target = (stage && stageDashboardMap[stage]) || '/dashboard1';
    navigate(target);
  };

  const desktop = (
    <motion.header
      className="py-4 sm:py-6 px-4 sm:px-8 md:px-16 lg:px-[140px] absolute top-10 md:top-14 left-0 right-0 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="flex justify-between items-center">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <Logo variant={isScrolled ? "default" : logoVariant} />
          </motion.div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-8">
            <NavLink to="/stylist" label="Our Stylists" isScrolled={isScrolled} logoVariant={logoVariant} />
            <ScrollLink to="features" label="Features" isScrolled={isScrolled} logoVariant={logoVariant} />
            <ScrollLink to="how-it-works" label="How It Works" isScrolled={isScrolled} logoVariant={logoVariant} />
            <ScrollLink to="faq" label="FAQ" isScrolled={isScrolled} logoVariant={logoVariant} />
            <NavLink to="/contact" label="Contact Us" isScrolled={isScrolled} logoVariant={logoVariant} />
            <div className="flex items-center gap-2 lg:gap-3">
              {user ? (
                <>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Button
                      onClick={handleDashboardClick}
                      className="bg-[#FF8B11] hover:bg-[#FF8B11]/90 text-white rounded-lg px-4 lg:px-6 py-2 text-sm font-medium"
                    >
                      Your Dashboard
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Button
                      onClick={signOut}
                      variant="ghost"
                      className={`border rounded-lg px-4 lg:px-6 py-2 text-sm font-medium ${isScrolled || logoVariant === "default"
                          ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
                          : 'border-white text-white hover:bg-white/10'
                        }`}
                    >
                      Sign Out
                    </Button>
                  </motion.div>
                </>
              ) : (
                <>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Link to="/auth">
                      <Button
                        variant="ghost"
                        className={`border rounded-md px-4 lg:px-6 h-[40px] uppercase tracking-[0.15em] text-[10px] font-bold transition-all ${isScrolled || logoVariant === "default"
                            ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
                            : 'border-white/30 text-white hover:bg-white/10 hover:border-white'
                          }`}
                      >
                        Login
                      </Button>
                    </Link>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    {onGetStartedClick ? (
                      <Button
                        onClick={onGetStartedClick}
                        className={`text-white rounded-md px-4 lg:px-6 h-[40px] uppercase tracking-[0.15em] text-[10px] font-bold border-b-2 active:translate-y-[1px] active:border-b-0 transition-all ${queued ? "bg-[#16A34A] hover:bg-[#15803D] border-[#0f7a37]" : "bg-[#FF8B11] hover:bg-[#FF8B11]/90 border-[#c2410c]"}`}
                      >
                        {queued ? "You're on the list" : "Join the waitlist"}
                      </Button>
                    ) : (
                      <Link to="/auth" state={{ tab: 'signup' }}>
                        <Button className="bg-[#FF8B11] hover:bg-[#FF8B11]/90 text-white rounded-md px-4 lg:px-6 h-[40px] uppercase tracking-[0.15em] text-[10px] font-bold border-b-2 border-[#c2410c] active:translate-y-[1px] active:border-b-0 transition-all">
                          Join the waitlist
                        </Button>
                      </Link>
                    )}
                  </motion.div>
                </>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                onClick={handleDashboardClick}
                className="bg-[#FF8B11] hover:bg-[#FF8B11]/90 text-white rounded-lg px-4 py-2 text-sm font-medium"
              >
                {user ? "Dashboard" : "Login"}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.header>
  );

  if (!mobileRedesign) return desktop;

  return (
    <>
      <MobileBar
        user={!!user}
        queued={queued}
        onGetStartedClick={onGetStartedClick}
        onDashboardClick={handleDashboardClick}
        onSignOut={signOut}
        pathname={location.pathname}
      />
      <div className="hidden md:block">{desktop}</div>
    </>
  );
};

// Mobile splash header (below md): in-flow brown bar — hamburger | white logo |
// Join — with a drop-down menu. Lives inside the page's sticky group, so it
// carries no positioning of its own.
interface MobileBarProps {
  user: boolean;
  queued: boolean;
  onGetStartedClick?: () => void;
  onDashboardClick: () => void;
  onSignOut: () => void;
  pathname: string;
}

const MENU_ROW = "flex min-h-[52px] items-center justify-between border-b border-white/15 text-[11px] font-bold uppercase tracking-[0.15em] text-white";
const MENU_OUTLINE_BTN = "flex h-11 flex-1 items-center justify-center rounded-md border border-white/50 text-[11px] font-bold uppercase tracking-[0.15em] text-white";
const MENU_FILLED_BTN = "flex h-11 flex-1 items-center justify-center rounded-md text-[11px] font-bold uppercase tracking-[0.15em] text-white active:translate-y-[2px] active:shadow-none";
const MENU_FILLED_ORANGE = `${MENU_FILLED_BTN} bg-[#FF8B11] shadow-[inset_0_-2px_0_#c2410c]`;
const MENU_FILLED_GREEN = `${MENU_FILLED_BTN} bg-[#16A34A] shadow-[inset_0_-2px_0_#0f7a37]`;

const MobileBar = ({ user, queued, onGetStartedClick, onDashboardClick, onSignOut, pathname }: MobileBarProps) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const close = () => setMenuOpen(false);

  // Close on route change and on Escape.
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        burgerRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const join = () => {
    close();
    if (onGetStartedClick) onGetStartedClick();
    else navigate('/auth', { state: { tab: 'signup' } });
  };

  // Close first so the absolute menu is gone when the browser measures the target.
  const scrollLink = (id: string, label: string) => (
    <a
      href={`#${id}`}
      onClick={(e) => { e.preventDefault(); close(); scrollToId(id); }}
      className={MENU_ROW}
    >
      {label}
      <span aria-hidden="true" className="font-playfair text-[18px] text-white/60">→</span>
    </a>
  );
  const routeLink = (to: string, label: string) => (
    <Link to={to} onClick={close} className={MENU_ROW}>
      {label}
      <span aria-hidden="true" className="font-playfair text-[18px] text-white/60">→</span>
    </Link>
  );

  return (
    <header className="md:hidden relative z-50 flex items-center justify-between px-4 py-3 bg-[rgba(120,53,15,0.88)] backdrop-blur-[14px]">
      <button
        ref={burgerRef}
        type="button"
        aria-label="Menu"
        aria-expanded={menuOpen}
        aria-controls="mobile-nav"
        onClick={() => setMenuOpen((o) => !o)}
        className="relative flex h-10 w-10 flex-col items-center justify-center gap-[5px] rounded-md border border-white/30 bg-white/10 before:absolute before:-inset-1 before:content-['']"
      >
        <span className={`block h-[1.5px] w-4 bg-white transition-transform ${menuOpen ? "translate-y-[6.5px] rotate-45" : ""}`} />
        <span className={`block h-[1.5px] w-4 bg-white transition-opacity ${menuOpen ? "opacity-0" : ""}`} />
        <span className={`block h-[1.5px] w-4 bg-white transition-transform ${menuOpen ? "-translate-y-[6.5px] -rotate-45" : ""}`} />
      </button>

      <div className="[&_img]:h-10 [&_div]:h-10">
        <Logo variant="white" />
      </div>

      <button
        type="button"
        onClick={user ? onDashboardClick : join}
        className={`relative h-10 rounded-md px-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-white before:absolute before:-inset-y-1 before:inset-x-0 before:content-[''] active:translate-y-[2px] active:shadow-none ${
          queued ? "bg-[#16A34A] shadow-[inset_0_-2px_0_#0f7a37]" : "bg-[#FF8B11] shadow-[inset_0_-2px_0_#c2410c]"
        }`}
      >
        {user ? "Dashboard" : queued ? "On the list" : "Join"}
      </button>

      <nav
        id="mobile-nav"
        aria-label="Menu"
        hidden={!menuOpen}
        className="absolute inset-x-0 top-full bg-[#78350F] px-4 pt-2 pb-6 shadow-[0_20px_40px_rgba(0,0,0,0.35)]"
      >
        <ul>
          <li>{routeLink("/stylist", "Our Stylists")}</li>
          <li>{scrollLink("features", "Features")}</li>
          <li>{scrollLink("how-it-works", "How it works")}</li>
          <li>{scrollLink("faq", "FAQ")}</li>
          <li>{routeLink("/contact", "Contact us")}</li>
        </ul>
        <div className="mt-5 flex gap-2.5">
          {user ? (
            <>
              <button type="button" onClick={() => { close(); onDashboardClick(); }} className={MENU_FILLED_ORANGE}>Dashboard</button>
              <button type="button" onClick={() => { close(); onSignOut(); }} className={MENU_OUTLINE_BTN}>Sign out</button>
            </>
          ) : (
            <>
              <Link to="/auth" onClick={close} className={MENU_OUTLINE_BTN}>Login</Link>
              <button type="button" onClick={join} className={queued ? MENU_FILLED_GREEN : MENU_FILLED_ORANGE}>
                {queued ? "You're on the list" : "Join the waitlist"}
              </button>
            </>
          )}
        </div>
      </nav>
    </header>
  );
};

const ScrollLink = ({ to, label, isScrolled, logoVariant }: { to: string; label: string; isScrolled?: boolean; logoVariant?: "default" | "white" }) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    scrollToId(to);
  };

  const isDarkText = isScrolled || logoVariant === "default";

  return (
    <motion.a
      href={`#${to}`}
      onClick={handleClick}
      className={`uppercase tracking-[0.15em] text-[10px] font-bold transition-colors duration-200 relative group text-center cursor-pointer ${isDarkText
          ? 'text-gray-700 hover:text-gray-900'
          : 'text-white hover:text-white/80'
        }`}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
    >
      {label}
      <span className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${isDarkText ? 'bg-gray-700' : 'bg-white'
        }`}></span>
    </motion.a>
  );
};

const MotionLink = motion(Link);

const NavLink = ({ to, label, isScrolled, logoVariant }: { to: string; label: string; isScrolled?: boolean; logoVariant?: "default" | "white" }) => {
  const isDarkText = isScrolled || logoVariant === "default";

  return (
    <MotionLink
      to={to}
      className={`uppercase tracking-[0.15em] text-[10px] font-bold transition-colors duration-200 relative group text-center ${isDarkText
          ? 'text-gray-700 hover:text-gray-900'
          : 'text-white hover:text-white/80'
        }`}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
    >
      {label}
      <span className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${isDarkText ? 'bg-gray-700' : 'bg-white'
        }`}></span>
    </MotionLink>
  );
};

export default Header;